import { useCallback, useEffect, useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  Gauge,
  ListChecks,
  Loader2,
  MessageSquare,
  RefreshCw,
  Search,
  Sparkles,
} from 'lucide-react'
import { usePageLayout } from '@/hooks/usePageLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import AuthAxios from '@/lib/api/axios/AuthAxios'
import { ENDPOINT_BASE } from '@/config/endpoints'
import useLoginStore from '@/store/LoginStore'

// Dev panel for semantic search + ask-your-workspace. Restricted via the backend's
// AiAccessOptions allowlist; the route also redirects in PROD builds (see beforeLoad).
// Backed by AiController in moly-platform: POST /api/v1.0/e/{slug}/ai/{index|search|ask}.
// Eventually replaced by the real Phase 2 "Smart" tab in SearchDialog + Phase 3 Ask dialog.

type SearchResult = {
  documentId: string
  title: string
  chunkIndex: number
  score: number
  snippet: string
}

type IndexResponse = {
  workspaceId: string
  indexedDocuments: number
  indexedChunks: number
  model?: string
  message?: string
}

type SearchResponse = {
  workspaceId: string
  query: string
  results: Array<SearchResult>
}

type AskResponse = {
  question: string
  answer: string
  model: string
  citations: Array<SearchResult> & Array<{ marker: string }>
}

type TaskProposal = {
  title: string
  description?: string | null
  ownerHint?: string | null
  dueHint?: string | null
  priority?: string | null
  evidence?: string | null
}

type ExtractResponse = {
  workspaceId: string
  documentId: string
  documentTitle: string | null
  model: string
  proposals: Array<TaskProposal>
}

type UsageResponse = {
  workspaceId: string
  billingPeriodStart: string
  totals: {
    calls: number
    successCalls: number
    inputTokens: number
    outputTokens: number
    totalTokens: number
    estimatedCostUsd: number
  }
  byKind: Array<{ kind: string; calls: number; tokens: number; costUsd: number }>
  byModel: Array<{ model: string; calls: number; tokens: number; costUsd: number }>
  byOperation: Array<{
    operation: string
    calls: number
    tokens: number
    costUsd: number
  }>
  recent: Array<{
    createdOn: string
    kind: string
    model: string
    operation: string | null
    inputTokens: number
    outputTokens: number
    totalTokens: number
    inputCount: number
    latencyMs: number
    success: boolean
    errorType: string | null
    estimatedCostUsd: number
  }>
}

function getErrorMessage(err: any) {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.title ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    'Request failed'
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/30 p-2">
      <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
        {label}
      </div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  )
}

export default function AiDevPage() {
  usePageLayout({ showHeader: false })

  // Read workspaceSlug from the store rather than Route.useParams() — the typed
  // params from the generated route tree require the dev server to have regenerated
  // it once after this file was added. The store is synced from the URL param so it's
  // equivalent at runtime.
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const base = `${ENDPOINT_BASE}/e/${workspaceSlug}/ai`

  // --- INDEX ----------------------------------------------------------------
  const [indexing, setIndexing] = useState(false)
  const [indexResult, setIndexResult] = useState<IndexResponse | null>(null)

  const handleIndex = async () => {
    setIndexing(true)
    setIndexResult(null)
    try {
      const res = await AuthAxios.post<IndexResponse>(`${base}/index`)
      setIndexResult(res.data)
      toast.success(
        `Indexed ${res.data.indexedDocuments} docs · ${res.data.indexedChunks} chunks`,
      )
    } catch (err: any) {
      toast.error(getErrorMessage(err))
    } finally {
      setIndexing(false)
    }
  }

  // --- SEARCH ---------------------------------------------------------------
  const [query, setQuery] = useState('')
  const [topK, setTopK] = useState(5)
  const [searching, setSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<Array<SearchResult>>([])

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error('Enter a query')
      return
    }
    setSearching(true)
    setSearchResults([])
    try {
      const res = await AuthAxios.post<SearchResponse>(`${base}/search`, {
        query: query.trim(),
        topK,
      })
      setSearchResults(res.data.results)
      if (res.data.results.length === 0) {
        toast.info('No matches — try indexing first or different wording')
      }
    } catch (err: any) {
      toast.error(getErrorMessage(err))
    } finally {
      setSearching(false)
    }
  }

  // --- ASK ------------------------------------------------------------------
  const [question, setQuestion] = useState('')
  const [asking, setAsking] = useState(false)
  const [askResult, setAskResult] = useState<AskResponse | null>(null)

  const handleAsk = async () => {
    if (!question.trim()) {
      toast.error('Enter a question')
      return
    }
    setAsking(true)
    setAskResult(null)
    try {
      const res = await AuthAxios.post<AskResponse>(`${base}/ask`, {
        question: question.trim(),
      })
      setAskResult(res.data)
    } catch (err: any) {
      toast.error(getErrorMessage(err))
    } finally {
      setAsking(false)
    }
  }

  // --- EXTRACT TASKS --------------------------------------------------------
  const [extractDocId, setExtractDocId] = useState('')
  const [extracting, setExtracting] = useState(false)
  const [extractResult, setExtractResult] = useState<ExtractResponse | null>(
    null,
  )

  const handleExtract = async () => {
    if (!extractDocId.trim()) {
      toast.error('Enter a document ID (copy from a search result)')
      return
    }
    setExtracting(true)
    setExtractResult(null)
    try {
      const res = await AuthAxios.post<ExtractResponse>(
        `${base}/extract-tasks`,
        { documentId: extractDocId.trim() },
      )
      setExtractResult(res.data)
      if (res.data.proposals.length === 0) {
        toast.info('No action items detected on that page')
      } else {
        toast.success(`Extracted ${res.data.proposals.length} proposal(s)`)
      }
    } catch (err: any) {
      toast.error(getErrorMessage(err))
    } finally {
      setExtracting(false)
    }
  }

  // --- USAGE ----------------------------------------------------------------
  const [usage, setUsage] = useState<UsageResponse | null>(null)
  const [usageLoading, setUsageLoading] = useState(false)

  const loadUsage = useCallback(async () => {
    if (!workspaceSlug) return
    setUsageLoading(true)
    try {
      const res = await AuthAxios.get<UsageResponse>(`${base}/usage`)
      setUsage(res.data)
    } catch (err: any) {
      toast.error(getErrorMessage(err))
    } finally {
      setUsageLoading(false)
    }
  }, [base, workspaceSlug])

  // Initial load + re-fetch whenever any AI action finishes (cheap; just sums
  // existing rows in Postgres).
  useEffect(() => {
    void loadUsage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workspaceSlug,
    indexResult,
    searchResults.length,
    askResult,
    extractResult,
  ])

  // --- UI -------------------------------------------------------------------
  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-6">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Sparkles className="size-5 text-purple-500" />
          <h1 className="text-2xl font-semibold">AI — dev panel</h1>
          <Badge variant="outline" className="ml-2">
            workspace: {workspaceSlug}
          </Badge>
        </div>
        <p className="text-muted-foreground text-sm">
          Throwaway UI for the semantic search + ask-your-workspace POC. Calls{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
            POST /api/v1.0/e/{workspaceSlug}/ai/*
          </code>
          .
        </p>
      </div>

      {/* USAGE — this month */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Gauge className="size-4" />
              Usage this month
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void loadUsage()}
              disabled={usageLoading}
              aria-label="Refresh usage"
            >
              <RefreshCw
                className={`size-3.5 ${usageLoading ? 'animate-spin' : ''}`}
              />
            </Button>
          </div>
          {usage && (
            <CardDescription>
              Period started{' '}
              {new Date(usage.billingPeriodStart).toLocaleDateString()}{' '}
              · Cloudflare AI Gateway dashboard is authoritative for billing.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {!usage ? (
            <div className="text-muted-foreground text-sm">
              {usageLoading ? 'Loading usage…' : 'No usage data yet.'}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Calls" value={usage.totals.calls.toLocaleString()} />
                <Stat
                  label="Total tokens"
                  value={usage.totals.totalTokens.toLocaleString()}
                />
                <Stat
                  label="Input / Output"
                  value={`${usage.totals.inputTokens.toLocaleString()} / ${usage.totals.outputTokens.toLocaleString()}`}
                />
                <Stat
                  label="Est. cost (USD)"
                  value={`$${usage.totals.estimatedCostUsd.toFixed(4)}`}
                />
              </div>

              {usage.byKind.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {usage.byKind.map((k) => (
                    <Badge key={k.kind} variant="outline" className="text-xs">
                      {k.kind}: {k.calls} call{k.calls === 1 ? '' : 's'} ·{' '}
                      {k.tokens.toLocaleString()} tok · ${k.costUsd.toFixed(4)}
                    </Badge>
                  ))}
                </div>
              )}

              {usage.byOperation.length > 0 && (
                <details className="text-xs">
                  <summary className="text-muted-foreground cursor-pointer">
                    By operation
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {usage.byOperation.map((o) => (
                      <li
                        key={o.operation}
                        className="flex items-center justify-between gap-2 font-mono"
                      >
                        <span className="truncate">{o.operation}</span>
                        <span className="text-muted-foreground shrink-0">
                          {o.calls} · {o.tokens.toLocaleString()} tok · $
                          {o.costUsd.toFixed(4)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* INDEX */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Loader2
              className={`size-4 ${indexing ? 'animate-spin' : 'hidden'}`}
            />
            1. Index workspace content
          </CardTitle>
          <CardDescription>
            Embeds every page (DocumentContent.PlainText) in this workspace.
            Idempotent — chunks with matching content hash are skipped.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={handleIndex} disabled={indexing}>
            {indexing ? 'Indexing…' : 'Index this workspace'}
          </Button>

          {indexResult && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div>
                <strong>Workspace:</strong>{' '}
                <code className="text-xs">{indexResult.workspaceId}</code>
              </div>
              <div>
                <strong>Documents:</strong> {indexResult.indexedDocuments}{' '}
                · <strong>Chunks:</strong> {indexResult.indexedChunks}
              </div>
              {indexResult.model && (
                <div>
                  <strong>Model:</strong>{' '}
                  <code className="text-xs">{indexResult.model}</code>
                </div>
              )}
              {indexResult.message && (
                <div className="text-muted-foreground mt-1 text-xs">
                  {indexResult.message}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SEARCH */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Search className="size-4" />
            2. Semantic search
          </CardTitle>
          <CardDescription>
            Returns one row per document (best-scoring chunk per doc). Score
            is cosine similarity in [0, 1].
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[260px]">
              <label className="text-muted-foreground mb-1 block text-xs">
                Query
              </label>
              <Input
                placeholder="e.g. meeting notes about pricing"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="w-20">
              <label className="text-muted-foreground mb-1 block text-xs">
                Top K
              </label>
              <Input
                type="number"
                min={1}
                max={20}
                value={topK}
                onChange={(e) => setTopK(Number(e.target.value) || 5)}
              />
            </div>
            <Button onClick={handleSearch} disabled={searching}>
              {searching ? 'Searching…' : 'Search'}
            </Button>
          </div>

          {searchResults.length > 0 && (
            <ul className="space-y-2">
              {searchResults.map((r) => (
                <li
                  key={`${r.documentId}-${r.chunkIndex}`}
                  className="rounded-md border p-3"
                >
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium">
                      {r.title || '(untitled)'}
                    </span>
                    <Badge variant="secondary" className="shrink-0">
                      {r.score.toFixed(3)}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    {r.snippet}
                  </p>
                  <div className="text-muted-foreground mt-1 font-mono text-[10px]">
                    {r.documentId} · chunk {r.chunkIndex}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ASK */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="size-4" />
            3. Ask your workspace (RAG)
          </CardTitle>
          <CardDescription>
            Retrieval-augmented answer with inline citations [1], [2]…
            mapping to the chunks listed below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            placeholder="e.g. What did we decide about the Stripe migration?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
          />
          <Button onClick={handleAsk} disabled={asking}>
            {asking ? 'Thinking…' : 'Ask'}
          </Button>

          {askResult && (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/40 p-3 text-sm whitespace-pre-wrap">
                {askResult.answer}
              </div>

              {askResult.citations?.length > 0 && (
                <div>
                  <div className="text-muted-foreground mb-1 text-xs font-semibold uppercase">
                    Sources
                  </div>
                  <ul className="space-y-2">
                    {askResult.citations.map((c, i) => (
                      <li
                        key={`${c.documentId}-${c.chunkIndex}`}
                        className="rounded-md border p-3"
                      >
                        <div className="mb-1 flex items-center gap-2">
                          <Badge variant="outline">[{i + 1}]</Badge>
                          <span className="truncate text-sm font-medium">
                            {c.title || '(untitled)'}
                          </span>
                          {typeof c.score === 'number' && (
                            <Badge variant="secondary" className="ml-auto">
                              {c.score.toFixed(3)}
                            </Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground text-xs leading-relaxed">
                          {c.snippet}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="text-muted-foreground text-[10px]">
                model: {askResult.model}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* EXTRACT TASKS FROM A PAGE */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ListChecks className="size-4" />
            4. Extract tasks from a page (agentic)
          </CardTitle>
          <CardDescription>
            Reads a page's plaintext, asks the model for concrete action items
            with owner/due/priority hints, and drops any proposal whose evidence
            quote isn't actually in the page. Returns proposals only — task
            creation is a separate step (coming next).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[260px]">
              <label className="text-muted-foreground mb-1 block text-xs">
                Document ID (copy from a search result)
              </label>
              <Input
                placeholder="e.g. 2652cc8e-e181-40a8-9f78-7e2d14c0eeab"
                value={extractDocId}
                onChange={(e) => setExtractDocId(e.target.value)}
              />
            </div>
            <Button onClick={handleExtract} disabled={extracting}>
              {extracting ? 'Extracting…' : 'Extract tasks'}
            </Button>
          </div>

          {extractResult && (
            <div className="space-y-3">
              <div className="text-muted-foreground text-xs">
                Page:{' '}
                <span className="font-medium text-foreground">
                  {extractResult.documentTitle || '(untitled)'}
                </span>
                {' · '}
                {extractResult.proposals.length} proposal
                {extractResult.proposals.length === 1 ? '' : 's'}
              </div>

              {extractResult.proposals.length === 0 ? (
                <div className="text-muted-foreground rounded-md border border-dashed p-3 text-sm">
                  No action items detected — the page may be informational, or
                  the items lacked clear ownership/intent.
                </div>
              ) : (
                <ul className="space-y-2">
                  {extractResult.proposals.map((p, i) => (
                    <li key={i} className="rounded-md border p-3">
                      <div className="mb-1 flex items-start justify-between gap-2">
                        <span className="text-sm font-medium">{p.title}</span>
                        {p.priority && (
                          <Badge
                            variant={
                              p.priority === 'high'
                                ? 'destructive'
                                : p.priority === 'medium'
                                  ? 'default'
                                  : 'secondary'
                            }
                            className="shrink-0 capitalize"
                          >
                            {p.priority}
                          </Badge>
                        )}
                      </div>

                      {p.description && (
                        <p className="text-muted-foreground mb-2 text-xs leading-relaxed">
                          {p.description}
                        </p>
                      )}

                      <div className="mb-2 flex flex-wrap gap-1.5">
                        {p.ownerHint && (
                          <Badge variant="outline" className="text-xs">
                            owner: {p.ownerHint}
                          </Badge>
                        )}
                        {p.dueHint && (
                          <Badge variant="outline" className="text-xs">
                            due: {p.dueHint}
                          </Badge>
                        )}
                      </div>

                      {p.evidence && (
                        <blockquote className="text-muted-foreground border-l-2 pl-2 text-xs italic">
                          “{p.evidence}”
                        </blockquote>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <div className="text-muted-foreground text-[10px]">
                model: {extractResult.model}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/ai-dev',
)({
  beforeLoad: ({ params }) => {
    // Hide in production builds — dev panel only.
    if (import.meta.env.PROD) {
      throw redirect({ to: '/$workspaceSlug', params })
    }
  },
  component: AiDevPage,
})
